/* Minimalista's bundled Swiper 9 is loaded by layout/theme.liquid. */
(() => {
  if (!customElements.get('bc-flex-picture')) {
    class BCFlexPicture extends HTMLElement {
      connectedCallback() {
        if (this.abortController) return;
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        queueMicrotask(() => {
          if (signal.aborted) return;
          this.image = this.querySelector('img');
          this.mobileSource = this.querySelector('source');
          if (!this.image) return;
          const update = () => this.updateSizes();
          this.image.addEventListener('load', update, { signal });
          window.addEventListener('resize', update, { signal });
          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(update);
            this.resizeObserver.observe(this.image);
          }
          update();
        });
      }
      updateSizes() {
        if (!this.image) return;
        const { width, height } = this.image.getBoundingClientRect();
        if (width <= 0) return;
        const cover = getComputedStyle(this.image).objectFit === 'cover';
        const sizeFor = ratio => Math.ceil(cover ? Math.max(width, height * (Number(ratio) || 1)) : width) + 'px';
        const desktopSize = sizeFor(this.dataset.desktopRatio);
        if (this.image.sizes !== desktopSize) this.image.sizes = desktopSize;
        if (this.mobileSource) {
          const mobileSize = sizeFor(this.dataset.mobileRatio);
          if (this.mobileSource.sizes !== mobileSize) this.mobileSource.sizes = mobileSize;
        }
      }
      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
      }
    }
    customElements.define('bc-flex-picture', BCFlexPicture);
  }
  if (customElements.get('bc-flex-slider')) return;
  class BCFlexSlider extends HTMLElement {
    static get observedAttributes() { return ['data-autoplay', 'data-speed', 'data-respect-reduced-motion']; }
    get transitionSpeed() {
      if (this.dataset.respectReducedMotion === 'true' && this.motion?.matches) return 0;
      const speed = Number(this.dataset.speed);
      return Number.isFinite(speed) && speed > 0 ? speed : 700;
    }
    attributeChangedCallback(name, previous, current) {
      if (!this.swiper || previous === current) return;
      if (name === 'data-autoplay') {
        this.userPaused = false;
        this.swiper.params.autoplay.enabled = current === 'true';
      } else {
        this.swiper.params.speed = this.transitionSpeed;
      }
      this.syncAutoplay();
    }
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.editorPaused = false;
      this.resumeThroughHover = false;
      this.resumeThroughFocus = false;
      this.hovered = false;
      this.inView = !('IntersectionObserver' in window);
      this.addEventListener('mouseenter', () => {
        this.hovered = true;
        this.resumeThroughHover = false;
        this.syncAutoplay();
      }, { signal: this.signal });
      this.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.resumeThroughHover = false;
        this.syncAutoplay();
      }, { signal: this.signal });
      this.addEventListener('focusin', () => {
        this.resumeThroughFocus = false;
        this.syncAutoplay();
      }, { signal: this.signal });
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (!this.contains(document.activeElement)) this.resumeThroughFocus = false;
        this.syncAutoplay();
      }), { signal: this.signal });
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal: this.signal });
      this.motion.addEventListener('change', () => {
        if (this.swiper) this.swiper.params.speed = this.transitionSpeed;
        this.syncAutoplay();
      }, { signal: this.signal });
      this.addEventListener('shopify:block:select', (event) => {
        if (!this.swiper) return;
        const slide = event.target.closest('.bc-flex__slide');
        if (slide && this.swiper.slides.includes(slide)) {
          this.editorPaused = true;
          this.swiper.slideTo(this.swiper.slides.indexOf(slide), 0);
          this.syncAutoplay();
        }
      }, { signal: this.signal });
      this.addEventListener('shopify:block:deselect', (event) => {
        const slide = event.target.closest('.bc-flex__slide');
        if (slide && this.swiper?.slides.includes(slide)) {
          this.editorPaused = false;
          this.syncAutoplay();
        }
      }, { signal: this.signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.teardown();
      }, { signal: this.signal });
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true, signal: this.signal });
      } else {
        this.init();
      }
      window.addEventListener('load', () => this.init(), { once: true, signal: this.signal });
    }
    init() {
      if (!this.isConnected || this.swiper || typeof window.Swiper !== 'function') return;
      const viewport = this.querySelector(':scope > .bc-flex__slider-viewport');
      const track = viewport?.querySelector(':scope > .bc-flex__slides');
      const slides = track ? Array.from(track.children).filter(el => el.classList.contains('bc-flex__slide')) : [];
      this.controls = this.querySelector(':scope > .bc-flex__controls');
      if (slides.length < 2) return;
      const prev = this.controls.querySelector('.bc-flex__prev');
      const next = this.controls.querySelector('.bc-flex__next');
      const dots = this.controls.querySelector('.bc-flex__pagination');
      this.pause = this.controls.querySelector('.bc-flex__pause');
      // Rewind preserves slide DOM/IDs and supports two slides with Swiper 9.
      this.swiper = new window.Swiper(viewport, {
        slidesPerView: 1,
        slidesPerGroup: 1,
        speed: this.transitionSpeed,
        effect: this.dataset.effect === 'fade' ? 'fade' : 'slide',
        fadeEffect: { crossFade: true },
        rewind: this.dataset.loop === 'true',
        loop: false,
        nested: true,
        watchSlidesProgress: true,
        autoHeight: false,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        navigation: prev && next ? { prevEl: prev, nextEl: next } : false,
        pagination: dots ? {
          el: dots, clickable: true,
          renderBullet(index, className) {
            return '<button type="button" class="' + className + '" aria-label="Go to slide ' + (index + 1) + '"></button>';
          }
        } : false,
        a11y: { enabled: true, slideLabelMessage: '{{index}} of {{slidesLength}}' },
        autoplay: {
          enabled: this.dataset.autoplay === 'true',
          delay: Number(this.dataset.delay) || 5000,
          disableOnInteraction: false,
          // Zero-duration transitions do not emit transitionend.
          waitForTransition: false,
          stopOnLastSlide: this.dataset.loop !== 'true'
        }
      });
      this.controls.hidden = !(prev || next || dots || this.pause);
      this.syncControls();
      if ('ResizeObserver' in window) {
        this.controlsObserver = new ResizeObserver(() => this.syncControls());
        this.controlsObserver.observe(this.controls);
      } else {
        window.addEventListener('resize', () => this.syncControls(), { signal: this.signal });
      }
      this.swiper.on('slideChange', () => {
        this.syncSlides();
        // Ending a non-repeating carousel is different from a temporary hover/focus pause.
        if (this.dataset.autoplay === 'true' && this.dataset.loop !== 'true' && this.swiper.isEnd && !this.editorPaused) {
          this.userPaused = true;
          this.syncAutoplay();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.target.matches('input, textarea, select, [contenteditable]') || event.altKey || event.ctrlKey || event.metaKey) return;
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.userPaused = true;
        const forward = (event.key === 'ArrowRight') !== this.swiper.rtlTranslate;
        forward ? this.swiper.slideNext() : this.swiper.slidePrev();
        this.syncAutoplay();
      }, { signal: this.signal });
      this.pause?.addEventListener('click', () => {
        const resume = this.userPaused || this.editorPaused;
        this.userPaused = !resume;
        if (resume) {
          this.editorPaused = false;
          // An explicit Play click must work while its button is hovered/focused.
          // The next new hover or focus interaction restores automatic pausing.
          this.resumeThroughHover = this.hovered;
          this.resumeThroughFocus = this.contains(document.activeElement);
          if (this.swiper.isEnd && this.dataset.loop !== 'true') this.swiper.slideTo(0);
        } else {
          this.resumeThroughHover = false;
          this.resumeThroughFocus = false;
        }
        this.syncAutoplay();
      }, { signal: this.signal });
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.syncAutoplay();
        }, { threshold: 0.1 });
        this.intersectionObserver.observe(this);
      }
      this.syncSlides();
      this.syncAutoplay();
    }
    syncControls() {
      const visible = this.controls && !this.controls.hidden;
      this.dataset.controlsVisible = String(Boolean(visible));
      const height = visible ? Math.ceil(this.controls.getBoundingClientRect().height) : 0;
      if (height !== this.controlsHeight) {
        this.controlsHeight = height;
        this.style.setProperty('--bc-controls-height', height + 'px');
        this.swiper?.update();
      }
    }
    syncSlides() {
      if (!this.swiper) return;
      this.swiper.slides.forEach((slide, index) => {
        const inactive = index !== this.swiper.activeIndex;
        slide.inert = inactive;
        slide.setAttribute('aria-hidden', String(inactive));
      });
    }
    updatePauseButton() {
      if (!this.pause) return;
      const paused = this.userPaused || this.editorPaused;
      this.pause.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      this.pause.querySelector('[data-pause-label]').textContent = paused ? 'Play' : 'Pause';
    }
    syncAutoplay() {
      if (!this.swiper) return;
      if (this.dataset.autoplay !== 'true') {
        this.swiper.autoplay.stop();
        this.querySelector('.bc-flex__slides').setAttribute('aria-live', 'polite');
        return;
      }
      const stopped = this.userPaused || this.editorPaused ||
        (this.hovered && !this.resumeThroughHover) ||
        (this.contains(document.activeElement) && !this.resumeThroughFocus) ||
        document.hidden || !this.inView;
      if (stopped) this.swiper.autoplay.stop();
      else if (!this.swiper.autoplay.running) this.swiper.autoplay.start();
      this.querySelector('.bc-flex__slides').setAttribute('aria-live', stopped ? 'polite' : 'off');
      this.updatePauseButton();
    }
    teardown() {
      this.abortController?.abort();
      this.abortController = null;
      this.intersectionObserver?.disconnect();
      this.controlsObserver?.disconnect();
      this.controlsHeight = null;
      delete this.dataset.controlsVisible;
      this.style.removeProperty('--bc-controls-height');
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.querySelectorAll('.bc-flex__slide').forEach(slide => {
        slide.inert = false;
        slide.removeAttribute('aria-hidden');
      });
      if (this.controls) this.controls.hidden = true;
    }
    disconnectedCallback() { this.teardown(); }
  }
  customElements.define('bc-flex-slider', BCFlexSlider);
})();
